import { useState, useCallback } from 'react';
import { Button, Checkbox, Input, Typography } from 'antd';
import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';

import PageWrapper from '../../components/MainPageWrapper/PageWrapper';
import { APP_URL } from '../../constants/common-app.const';
import {
  AVAILABLE_FOR_OPTIONS,
  AvailableForValue,
  COURSES_QUERY_PARAMS,
} from './Courses.const';
import useGetMyContracts from '../../hooks/useGetMyContracts';

import css from './Courses.module.css';
import CoursesCards from '../../components/CoursesCards/CoursesCards';

const Courses = () => {
  const { Title } = Typography;
  const history = useHistory();
  const { pathname, search } = useLocation();
  const [count, setCount] = useState<number | null>(null);
  const handleCount = useCallback((n: number) => setCount(n), []);
  const { contracts } = useGetMyContracts();
  const hasContracts = contracts.length > 0;
  const searchParams = new URLSearchParams(search);

  const searchValue = searchParams.get(COURSES_QUERY_PARAMS.SEARCH) ?? '';
  const availableForRaw =
    searchParams.get(COURSES_QUERY_PARAMS.AVAILABLE_FOR) ?? '';
  const validValues = AVAILABLE_FOR_OPTIONS.map(
    (o) => o.value,
  ) as readonly string[];
  const availableForValues = availableForRaw
    .split(',')
    .filter((v) => validValues.includes(v)) as AvailableForValue[];

  const updateParams = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(search);
    mutate(next);
    const qs = next.toString();
    history.replace(qs ? `${pathname}?${qs}` : pathname);
  };

  const handleSearchChange = (value: string) => {
    updateParams((next) => {
      if (value) {
        next.set(COURSES_QUERY_PARAMS.SEARCH, value);
      } else {
        next.delete(COURSES_QUERY_PARAMS.SEARCH);
      }
    });
  };

  const handleAvailableForChange = (value: AvailableForValue) => {
    const set = new Set(availableForValues);
    if (set.has(value)) {
      set.delete(value);
    } else {
      set.add(value);
    }
    updateParams((next) => {
      const joined = Array.from(set).join(',');
      if (joined) {
        next.set(COURSES_QUERY_PARAMS.AVAILABLE_FOR, joined);
      } else {
        next.delete(COURSES_QUERY_PARAMS.AVAILABLE_FOR);
      }
    });
  };

  return (
    <PageWrapper>
      <div className={css.SelectWrapper}>
        <Title level={2} style={{ color: '#1677ff' }}>
          Курсы
        </Title>

        {hasContracts && (
          <div className={css.Banner}>
            <div className={css.BannerInfo}>
              <span className={css.BannerIcon}>
                <InfoCircleOutlined />
              </span>
              <span className={css.BannerText}>
                У вас уже есть оформленные курсы
              </span>
            </div>
            <div className={css.BannerActions}>
              <Button
                className={css.BannerBtn}
                onClick={() => history.push(APP_URL.MY_COURSES)}
              >
                Перейти в мои курсы
              </Button>
            </div>
          </div>
        )}

        <div className={css.Filters}>
          <Input
            className={css.Search}
            placeholder='Поиск по названию или описанию курса'
            prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
            allowClear
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <div className={css.AvailableFor}>
            <span className={css.AvailableLabel}>Доступно для:</span>
            {AVAILABLE_FOR_OPTIONS.map((opt) => (
              <Checkbox
                key={opt.value}
                checked={availableForValues.includes(opt.value)}
                onChange={() => handleAvailableForChange(opt.value)}
              >
                {opt.label}
              </Checkbox>
            ))}
          </div>
        </div>

        <Title level={5} className={css.SpecTitle}>
          Найдено курсов: {count ?? '…'}
        </Title>

        <CoursesCards
          search={searchValue}
          availableFor={availableForValues}
          onCount={handleCount}
        />
      </div>
    </PageWrapper>
  );
};

export default Courses;
